import { IObjectDevicesComponents } from '@ucheba/utils/helpers/components/types'
import dynamic from 'next/dynamic'
import { NextSeo } from 'next-seo'
import { NextPageContext } from 'next/dist/shared/lib/utils'
import { Store } from 'redux'
import { NextPage } from 'next'
import { useSelector } from 'react-redux'
import {
  newOlympSearchSelectors,
  newOlympSearchThunks,
} from '@ucheba/store/olympiads-new/search'
import React from 'react'
import { getIDeviceComponent } from '@ucheba/utils/helpers/components'
import { olympsIdSelectors, olympsIdThunks } from '@ucheba/store/olympiads-new/id'
import { useRedirectByQueryParams } from '../../../../../utils/hooks/useRedirectByQueryParams'
import { olymp } from '../../../../routs'
import Custom404 from '../../../404.page'

const devicesPagesFriendlyUrl: IObjectDevicesComponents = {
  desktop: dynamic(() => import('../search/desktop/NewOlympSearchPage')),
  touch: dynamic(() => import('../search/touch/NewOlympSearchPage')),
}

const devicesPagesById: IObjectDevicesComponents = {
  desktop: dynamic(() => import('../[id]/desktop')),
  touch: dynamic(() => import('../[id]/touch')),
}

const NewOlympSearchPage: NextPage<any> = (props) => {
  const { content, notFound, noindex, nofollow, isOlympPage } = props

  const entity = useSelector(newOlympSearchSelectors.entityByFriendlyUrl)

  /*
   * Если isOlympPage - возвращаем олимпиады, и не нужно делать редирект
   * isOlympPage = false - отработает ЧПУ урл
   */
  useRedirectByQueryParams({ pathname: olymp.path, skip: isOlympPage })

  if (notFound) {
    return <Custom404 />
  }

  if (isOlympPage) {
    return (
      <>
        <NextSeo title={content.seo.title} description={content.seo.description} />

        {getIDeviceComponent(props, devicesPagesById)}
      </>
    )
  }

  return (
    <>
      <NextSeo
        title={content.seo.title}
        description={content.seo.description}
        nofollow={!!nofollow}
        noindex={!!noindex}
        openGraph={{
          title: content.seo.title,
          description: content.seo.description,
        }}
      />

      {getIDeviceComponent(
        { ...props, content: { ...content, result: entity?.result } },
        devicesPagesFriendlyUrl
      )}
    </>
  )
}

/*
 * Объединили страницы (карточка олимпиады/чпу поиска)
 * Чтобы для ЧПУ отрабатывал такой урл: /olympiads/uz-1153
 * Без этого такой урл: /olympiads/uz-1153 воспринимается как id для карточки олимпиады и возвращается 404
 */
NewOlympSearchPage.getInitialProps = async (
  ctx: NextPageContext & { store: Store }
): Promise<any> => {
  const { store, query, res } = ctx
  const { dispatch, getState } = store
  const { params } = query

  /*
   * Если 1 параметр и этот параметр с типом number, отрабатывает карточка олимпиады с id
   * Если NaN - отрабатывает ЧПУ запрос
   */
  if (params && params.length === 1) {
    const id = Number(params[0])
    if (!Number.isNaN(id)) {
      await dispatch<any>(olympsIdThunks.fetch({ ctx, data: { id: String(id) } }))

      const olympiads = olympsIdSelectors.entity(getState())

      const { name, classFrom, classTo } = olympiads || {}

      const seoDescription = `${name} для учеников ${classFrom === classTo ? `${classFrom} класса` : `${classFrom}-${classTo} классов`}: предметы, график этапов, льготы при поступлении в вуз `

      if (!olympiads) {
        if (res) {
          res.statusCode = 404
        }

        return {
          notFound: true,
        }
      }

      return {
        isOlympPage: true,
        content: {
          seo: {
            title: `${name} — Учёба.ру`,
            description: seoDescription,
          },
        },
      }
    }
  }

  // такой урл /search/uz-1153 воспринимается как параметр для ЧПУ
  if (Array.isArray(params) && params[0] === 'search') {
    return {
      notFound: true,
    }
  }

  const searchPath = (() => {
    if (Array.isArray(params)) {
      return `/${params.join('/')}`
    }
    return ''
  })()

  await dispatch(
    newOlympSearchThunks.fetchByFriendlyURL({
      ctx,
      data: {
        limit: 15,
        url: searchPath,
      },
    })
  )

  const entityByFriendlyUrl = newOlympSearchSelectors.entityByFriendlyUrl(getState())

  const { seo, items } = entityByFriendlyUrl?.result || {}

  if (!items) {
    if (res) {
      res.statusCode = 404
    }

    return {
      notFound: true,
    }
  }

  return {
    content: {
      result: entityByFriendlyUrl?.result,
      seo: {
        h1: seo?.h1 || 'Олимпиады — Учёба.ру',
        title: seo?.title || 'Олимпиады — Учёба.ру',
        description: seo?.description || 'Олимпиады — Учеба.ру',
      },
    },
    nofollow:
      typeof entityByFriendlyUrl?.canIndex === 'boolean'
        ? !entityByFriendlyUrl.canIndex
        : false,
    noindex:
      typeof entityByFriendlyUrl?.canIndex === 'boolean'
        ? !entityByFriendlyUrl.canIndex
        : false,
  }
}

export default NewOlympSearchPage
